import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Instagram, Globe, Sparkles, ArrowRight, Store, Users, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import LoadingScreen from "../components/LoadingScreen";
import { useLanguage, LanguageSwitcher } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const exampleStores = [
  { name: "Urban Style", image: "https://images.pexels.com/photos/12025472/pexels-photo-12025472.jpeg", type: "instagram" },
  { name: "Tech Startup", image: "https://images.pexels.com/photos/8217473/pexels-photo-8217473.jpeg", type: "website" },
  { name: "Creative Studio", image: "https://images.pexels.com/photos/9869067/pexels-photo-9869067.jpeg", type: "instagram" }
];

const brandLogos = ["Nike", "Adidas", "Apple", "Google", "Meta", "Spotify", "Netflix", "Amazon"];

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [sourceType, setSourceType] = useState("instagram");
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ total_stores: 1247, stores_today: 23 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  };

  const handleGenerate = async () => {
    if (!url.trim()) {
      toast.error(t('language') === 'fr' ? "Veuillez entrer une URL" : "Please enter a URL");
      return;
    }
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/generate-store`, {
        url: url.trim(),
        source_type: sourceType
      });

      if (response.data && response.data.id) {
        navigate(`/store/${response.data.id}`, { state: { store: response.data } });
      }
    } catch (error) {
      console.error("Generation failed:", error);
      if (error.response?.status === 429) {
        toast.error(t('language') === 'fr' ? "Limite atteinte. Réessayez plus tard." : "Rate limit exceeded. Try again later.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate store");
      }
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl">InstaMerch</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#how-it-works" className="hidden md:block text-sm text-gray-600 hover:text-black transition-colors">
              {t('howItWorks')}
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered Merchandise
              </span>
              
              <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
                {t('heroTitle')}{" "}
                <span className="gradient-text">{t('heroTitleHighlight')}</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-lg">
                {t('heroSubtitle')}
              </p>

              {/* Input Tabs */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100" data-testid="url-input-section">
                <Tabs value={sourceType} onValueChange={setSourceType} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6 bg-white">
                    <TabsTrigger value="instagram" className="flex items-center gap-2 data-[state=active]:bg-black data-[state=active]:text-white" data-testid="instagram-tab">
                      <Instagram className="w-4 h-4" />
                      {t('tabInstagram')}
                    </TabsTrigger>
                    <TabsTrigger value="website" className="flex items-center gap-2 data-[state=active]:bg-black data-[state=active]:text-white" data-testid="website-tab">
                      <Globe className="w-4 h-4" />
                      {t('tabWebsite')}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="instagram" className="mt-0">
                    <Input
                      type="text"
                      placeholder={t('placeholderInstagram')}
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="h-14 text-lg bg-white border-gray-200 focus:ring-2 focus:ring-purple-500/20"
                      data-testid="instagram-input"
                    />
                  </TabsContent>
                  
                  <TabsContent value="website" className="mt-0">
                    <Input
                      type="text"
                      placeholder={t('placeholderWebsite')}
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="h-14 text-lg bg-white border-gray-200 focus:ring-2 focus:ring-purple-500/20"
                      data-testid="website-input"
                    />
                  </TabsContent>
                </Tabs>

                <Button 
                  onClick={handleGenerate}
                  className="w-full h-14 mt-4 bg-black hover:bg-gray-800 text-white rounded-xl text-lg font-medium group"
                  data-testid="generate-button"
                >
                  {t('generateButton')}
                  <Sparkles className="w-5 h-5 ml-2 group-hover:rotate-12 transition-transform" />
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="w-4 h-4 text-green-500" />
                  <span><strong>{(stats?.total_stores || 0).toLocaleString()}</strong> {t('storesCreated')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  <span><strong>{stats?.stores_today || 0}</strong> {t('today')}</span>
                </div>
              </div>
            </motion.div>

            {/* Right Content */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
              <div className="relative">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                  <div className="bg-gradient-to-br from-purple-50 to-white p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full" />
                      <div>
                        <h3 className="font-semibold text-gray-900">Urban Style Co.</h3>
                        <p className="text-sm text-gray-500">@urbanstyleco</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {exampleStores.map((store, i) => (
                        <motion.div key={i} className="aspect-square rounded-xl overflow-hidden bg-gray-100" whileHover={{ scale: 1.05 }}>
                          <img src={store.image} alt={store.name} className="w-full h-full object-cover" />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  <div className="p-6 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">9 products generated</span>
                      <span className="text-sm font-medium text-purple-600">View store →</span>
                    </div>
                  </div>
                </div>

                <motion.div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-4 border border-gray-100" animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-sm font-medium">Ready in 30s</span>
                  </div>
                </motion.div>

                <motion.div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 border border-gray-100" animate={{ y: [0, 8, 0] }} transition={{ duration: 3, repeat: Infinity, delay: 1 }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Store className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium">AI Generated</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-sm text-gray-400 mb-6">{t('trustedBy')}</p>
          <Marquee gradient gradientColor="#FAFAFA" speed={40} className="overflow-hidden">
            <div className="flex items-center gap-16 px-8">
              {brandLogos.map((brand, i) => (
                <span key={i} className="text-2xl font-bold text-gray-300 whitespace-nowrap">{brand}</span>
              ))}
            </div>
          </Marquee>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('howItWorks')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('howItWorksSubtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: t('step1Title'), description: t('step1Desc'), icon: Globe },
              { step: "02", title: t('step2Title'), description: t('step2Desc'), icon: Sparkles },
              { step: "03", title: t('step3Title'), description: t('step3Desc'), icon: Store }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative group">
                <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300">
                  <span className="text-6xl font-display font-bold text-gray-100 absolute top-4 right-6">{item.step}</span>
                  <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('ctaTitle')}</h2>
            <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">{t('ctaSubtitle')}</p>
            <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="h-14 px-10 bg-white text-gray-900 hover:bg-gray-100 rounded-full text-lg font-medium group" data-testid="cta-button">
              {t('ctaButton')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl">InstaMerch</span>
            </div>
            <p className="text-sm text-gray-500">
              {t('poweredBy')} <a href="https://wiltee.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Wiltee</a>
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-gray-900 transition-colors">{t('terms')}</a>
              <a href="#" className="hover:text-gray-900 transition-colors">{t('privacy')}</a>
              <a href="#" className="hover:text-gray-900 transition-colors">{t('contact')}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
